import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useInertiaData from '@/hooks/useInertiaData';
import useThemeColors from '@/hooks/useThemeColors';
import AuthLayout from '@/layouts/AuthLayout';
import { Academy } from '@/types';
import { useForm } from '@inertiajs/react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface Props {
    userAcademies: Academy[];
}

const SelectAcademyContent = ({ userAcademies }: Props) => {
    const { user } = useInertiaData();
    const colors = useThemeColors();
    const { data, setData, post } = useForm({
        academy_id: 0,
    });

    const { t } = useTranslation();

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        post('/auth/select-academy/');
    };

    return (
        <AuthLayout>
            <PageTitle title="Seleccionar Academia" />
            <div className="w-full">
                <div className="mb-6 text-center">
                    <h1 className="mb-3 text-2xl font-semibold">
                        {t('common.welcome')} {user?.name}
                    </h1>
                </div>

                <div className="flex flex-col gap-y-5">
                    <div className="flex flex-col gap-y-2">
                        <h2 className="text-xl font-semibold">
                            {t('auth.selectAcademy.title')}
                        </h2>
                        <p className="text-gray-500">
                            {t('auth.selectAcademy.description')}
                        </p>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-col gap-y-2">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {userAcademies.map((academy) => (
                                    <label
                                        key={academy.id}
                                        className={clsx(
                                            'relative cursor-pointer rounded-lg border-2 p-4 transition-all',
                                            data.academy_id === academy.id
                                                ? `${colors.border} ${colors.accentBg}`
                                                : 'border-gray-200 bg-white hover:border-gray-300'
                                        )}
                                    >
                                        <input
                                            type="radio"
                                            name="academy"
                                            value={academy.id}
                                            checked={
                                                data.academy_id === academy.id
                                            }
                                            onChange={() =>
                                                setData(
                                                    'academy_id',
                                                    academy.id
                                                )
                                            }
                                            className="sr-only"
                                        />
                                        <img
                                            src={academy.logo_url}
                                            alt={academy.name}
                                            className="mx-auto h-auto w-full"
                                        />
                                        {data.academy_id === academy.id && (
                                            <div
                                                className={`absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full ${colors.primaryBg}`}
                                            >
                                                <svg
                                                    className="h-3 w-3 text-white"
                                                    fill="currentColor"
                                                    viewBox="0 0 12 12"
                                                >
                                                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                                                </svg>
                                            </div>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <PrimaryButton
                            type="submit"
                            disabled={!data.academy_id}
                            className="mt-10"
                        >
                            {t('common.continue')}
                        </PrimaryButton>
                    </form>
                </div>
            </div>
        </AuthLayout>
    );
};

const SelectAcademy = ({ userAcademies }: Props) => {
    return (
        <ThemeWrapper>
            <SelectAcademyContent userAcademies={userAcademies} />
        </ThemeWrapper>
    );
};

export default SelectAcademy;
